import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { RateLimitError } from '../../lib/api/client';
import type { EmailLoginChallenge } from '../../types';

interface Props {
  challenge: EmailLoginChallenge;
  /** A new code replaced the old one (resend): keep the new challenge id. */
  onChallengeChange: (c: EmailLoginChallenge) => void;
  /** Finishes the sign-in with the code. Rejects with an Error (optionally carrying `code`) to show. */
  onComplete: (code: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * The sign-in step for a self-registered account that hasn't proved its email yet (medopd-docs 96): the code was
 * emailed to the account's address (shown masked); entering it finishes the sign-in (or leads to MFA).
 */
export function EmailLoginCodeStep({ challenge, onChallengeChange, onComplete, onCancel }: Props) {
  const { t } = useTranslation('auth');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(challenge.resendAfterSeconds ?? 30);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const failed = t('emailVerify.failed', 'Something went wrong. Please try again.');

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await onComplete(code);
    } catch (err) {
      if (err instanceof RateLimitError) {
        setError(t('emailVerify.tooMany', 'Too many attempts. Please wait a little and try again.'));
      } else {
        const c = (err as { code?: string }).code;
        setError(err instanceof Error && err.message ? err.message : failed);
        if (c === 'CODE_EXPIRED') setCode('');
      }
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.emailVerification.resendLoginCode(challenge.pendingToken, challenge.challengeId);
      if (res.error || !res.data) throw new Error(res.error ?? failed);
      onChallengeChange({
        pendingToken: res.data.pendingToken, challengeId: res.data.challengeId, emailHint: res.data.emailHint,
        expiresInSeconds: res.data.expiresInSeconds, resendAfterSeconds: res.data.resendAfterSeconds,
      });
      setCode('');
      setResendIn(res.data.resendAfterSeconds ?? 30);
      setInfo(t('emailVerify.resent', 'A new code is on its way.'));
    } catch (err) {
      setError(err instanceof RateLimitError
        ? t('emailVerify.tooMany', 'Too many attempts. Please wait a little and try again.')
        : err instanceof Error ? err.message : failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box component="form" onSubmit={submit} noValidate>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 750 }}>{t('emailVerify.loginTitle', 'Confirm your email')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {t('emailVerify.loginLede', 'To keep your account safe, confirm the email address on it once. We sent a 6-digit code to {{hint}}.',
              { hint: challenge.emailHint })}
          </Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        {info && <Alert severity="info">{info}</Alert>}
        <TextField label={t('emailVerify.codeLabel', 'Code from the email')} value={code} id="email-login-code" autoFocus
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} disabled={busy}
          inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }} />
        <Button type="submit" variant="contained" size="large" disabled={busy || code.length !== 6}>
          {busy && <CircularProgress size={18} sx={{ mr: 1, color: 'inherit' }} />}
          {t('emailVerify.confirm', 'Confirm and continue')}
        </Button>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button onClick={resend} disabled={busy || resendIn > 0}>
            {resendIn > 0 ? t('emailVerify.resendIn', 'Resend in {{s}}s', { s: resendIn }) : t('emailVerify.resend', 'Resend code')}
          </Button>
          <Button color="inherit" onClick={onCancel} disabled={busy}>{t('action.backToSignIn')}</Button>
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('emailVerify.notYours', 'Not your email address? Don’t continue — contact ClinVea support.')}
        </Typography>
      </Stack>
    </Box>
  );
}
